import "@/App.css";
import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Lock } from "lucide-react";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import EarnedTrialModal from "@/components/EarnedTrialModal";
import { RouteMeta } from "@/components/RouteMeta";
import { ThemeProvider } from "@/context/ThemeContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import GuaranteePage from "@/pages/GuaranteePage";
import Dashboard from "@/pages/Dashboard";
import DiagnosticPage from "@/pages/DiagnosticPage";
import PracticePage from "@/pages/PracticePage";
import PlanTodayPage from "@/pages/PlanTodayPage";
import FlashcardsPage from "@/pages/FlashcardsPage";
import MockExamPage from "@/pages/MockExamPage";
import StudyPlanPage from "@/pages/StudyPlanPage";
import ProgressPage from "@/pages/ProgressPage";
import AchievementsPage from "@/pages/AchievementsPage";
import LearnPage from "@/pages/LearnPage";
import SubjectLearnPage from "@/pages/SubjectLearnPage";
import TopicDetailPage from "@/pages/TopicDetailPage";
import TrialsPage from "@/pages/TrialsPage";
import TrialRunnerPage from "@/pages/TrialRunnerPage";
import CommunityPage from "@/pages/CommunityPage";
import GuidesPage from "@/pages/GuidesPage";
const DynamicPage = lazy(() => import("@/pages/DynamicPage"));

// /guides/:slug — CMS guide first, else the built-in hard-coded guides page.
const GuideSlugRouter = () => {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const [mode, setMode] = useState("loading");
  useEffect(() => {
    // Admin draft preview: render the CMS guide directly so DynamicPage can
    // fetch the draft via the admin preview endpoint.
    if (preview) { setMode("cms"); return; }
    let m = true;
    setMode("loading");
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/pages/${encodeURIComponent(slug)}`)
      .then((r) => { if (m) setMode(r.data?.type === "guide" ? "cms" : "builtin"); })
      .catch(() => { if (m) setMode("builtin"); });
    return () => { m = false; };
  }, [slug, preview]);
  if (mode === "loading") return null;
  if (mode === "cms") return <Suspense fallback={null}><DynamicPage type="guide" /></Suspense>;
  return <GuidesPage />;
};
import ScenariosPage from "@/pages/ScenariosPage";
import ScenarioDetailPage from "@/pages/ScenarioDetailPage";
import OsceRolePlayPage from "@/pages/OsceRolePlayPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StudyTopicPage from "@/pages/StudyTopicPage";
import DailyReviewPage from "@/pages/DailyReviewPage";
import BillingPage from "@/pages/BillingPage";
import AccountPage from "@/pages/AccountPage";
import QuickReadinessPage from "@/pages/QuickReadinessPage";
import PricingPage from "@/pages/PricingPage";
import WelcomePage from "@/pages/WelcomePage";
import { AdcPart1Pillar, AdcPart1Questions, AdcPart1MockExam, AdcPart1StudyGuide } from "@/pages/AdcPart1Pages";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { TermsPage, PrivacyPage, RefundPage, ContactPage } from "@/pages/LegalPages";

// Admin
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboardPage from "@/admin/AdminDashboardPage";
import AdminUsersPage from "@/admin/AdminUsersPage";
import AdminTrackingPage from "@/admin/AdminTrackingPage";
import AdminMissedTrialsPage from "@/admin/AdminMissedTrialsPage";
import AdminMcqsPage from "@/admin/AdminMcqsPage";
import AdminScenariosPage from "@/admin/AdminScenariosPage";
import AdminTopicsPage from "@/admin/AdminTopicsPage";
import AdminAnnouncementsPage from "@/admin/AdminAnnouncementsPage";
import AdminPromoCodesPage from "@/admin/AdminPromoCodesPage";
import AdminSupportPage from "@/admin/AdminSupportPage";
import AdminTicketDetailPage from "@/admin/AdminTicketDetailPage";
import AdminLessonsReviewPage from "@/admin/AdminLessonsReviewPage";
import AdminLessonDetailPage from "@/admin/AdminLessonDetailPage";
import AdminPlansPage from "@/admin/AdminPlansPage";
import AdminContentSyncPage from "@/admin/AdminContentSyncPage";
import AdminMarketingPage from "@/admin/AdminMarketingPage";
import AdminAbandonedCheckoutsPage from "@/admin/AdminAbandonedCheckoutsPage";
import AdminFunnelPage from "@/admin/AdminFunnelPage";
import MagicLoginPage from "@/pages/MagicLoginPage";
import AdminSettingsPage from "@/admin/AdminSettingsPage";
import AdminSiteSettingsPage from "@/admin/AdminSiteSettingsPage";
import AdminSiteContentPage from "@/admin/AdminSiteContentPage";
import AdminLeadsPage from "@/admin/AdminLeadsPage";
import AdminQuestionAuditPage from "@/admin/AdminQuestionAuditPage";
import AdminQuestionRepairPage from "@/admin/AdminQuestionRepairPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import MockEventPage from "@/pages/MockEventPage";

// Components
import Sidebar from "@/components/Sidebar";
import MobileNav from "@/components/MobileNav";
import MentorChat from "@/components/MentorChat";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Ensure cookies travel on every request
axios.defaults.withCredentials = true;

// API helper with error handling
export const api = {
  get: async (endpoint) => (await axios.get(`${API}${endpoint}`)).data,
  post: async (endpoint, data) => (await axios.post(`${API}${endpoint}`, data)).data,
  put: async (endpoint, data) => (await axios.put(`${API}${endpoint}`, data)).data,
  patch: async (endpoint, data) => (await axios.patch(`${API}${endpoint}`, data)).data,
};

// Email watermark for anti-sharing (psychological deterrent #6)
const EmailWatermark = () => {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed bottom-2 left-1/2 -translate-x-1/2 z-40 text-[10px] text-white/5 font-mono select-none"
    >
      {user.email} · {user.id.slice(0, 8)}
    </div>
  );
};

// App Layout with Sidebar - only for authenticated app screens
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const publicRoutes = ["/", "/login", "/signup", "/forgot-password", "/reset-password", "/magic-login", "/terms", "/privacy", "/refund", "/contact", "/mock-event", "/readiness-check", "/pricing", "/adc-part-1", "/adc-part-1/questions", "/adc-part-1/mock-exam", "/adc-part-1/study-guide"];
  const protectedTop = ["/dashboard", "/practice", "/mock", "/learn", "/osce", "/community", "/study-plan", "/progress", "/achievements", "/billing", "/account", "/welcome", "/diagnostic"];
  const isSingleSegment = /^\/[^/]+$/.test(location.pathname);
  const isCmsCandidate = isSingleSegment && !protectedTop.includes(location.pathname) && !location.pathname.startsWith("/admin");
  const isPublic = publicRoutes.includes(location.pathname) || location.pathname.startsWith("/guides") || location.pathname.startsWith("/unsubscribe") || isCmsCandidate;
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Google OAuth callback - must intercept BEFORE any route/auth logic runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  // Admin section has its own shell - don't wrap in user-side sidebar
  if (isAdminRoute) return <>{children}</>;

  // /diagnostic is public for anonymous users (no sidebar), but for logged-in users it lives in the app shell
  if (isPublic) return <>{children}</>;
  if (location.pathname === "/diagnostic" && !user) return <>{children}</>;
  // /welcome is the full-screen guided onboarding - no sidebar
  if (location.pathname === "/welcome") return <>{children}</>;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 min-w-0 md:ml-20 pb-24 md:pb-0 overflow-x-hidden">
        <div className="p-4 md:p-8 max-w-7xl mx-auto min-w-0">{children}</div>
      </main>
      <MobileNav />
      <EmailWatermark />
    </div>
  );
};

// Billing success/cancel tiny redirect pages
const BillingSuccess = () => {
  const location = useLocation();
  const sid = new URLSearchParams(location.search).get("session_id");
  return <Navigate to={`/billing?from_session=${sid || ""}`} replace />;
};
const BillingCancel = () => <Navigate to="/billing" replace />;

function AppShell() {
  return (
    <BrowserRouter>
      <RouteMeta />
      <AnalyticsTracker />
      <AppLayout>
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/guarantee" element={<GuaranteePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/magic-login" element={<MagicLoginPage />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/billing/cancel" element={<BillingCancel />} />
          <Route path="/readiness-check" element={<QuickReadinessPage />} />
          <Route path="/readiness" element={<Navigate to="/readiness-check" replace />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/adc-part-1" element={<AdcPart1Pillar />} />
          <Route path="/adc-part-1/questions" element={<AdcPart1Questions />} />
          <Route path="/adc-part-1/mock-exam" element={<AdcPart1MockExam />} />
          <Route path="/adc-part-1/study-guide" element={<AdcPart1StudyGuide />} />

          {/* Authenticated */}
          <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/diagnostic" element={<DiagnosticPage />} />
          <Route path="/learn" element={<ProtectedRoute><LearnPage /></ProtectedRoute>} />
          <Route path="/learn/:subjectId" element={<ProtectedRoute><SubjectLearnPage /></ProtectedRoute>} />
          <Route path="/learn/:subjectId/:topicId" element={<ProtectedRoute><TopicDetailPage /></ProtectedRoute>} />
          <Route path="/study/:subjectId/:topicId" element={<ProtectedRoute><StudyTopicPage /></ProtectedRoute>} />
          <Route path="/review" element={<ProtectedRoute><DailyReviewPage /></ProtectedRoute>} />
          <Route path="/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
          <Route path="/practice/:subjectId" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
          <Route path="/plan/today" element={<ProtectedRoute><PlanTodayPage /></ProtectedRoute>} />
          <Route path="/cases" element={<ProtectedRoute><ScenariosPage /></ProtectedRoute>} />
          <Route path="/cases/:scenarioId" element={<ProtectedRoute><ScenarioDetailPage /></ProtectedRoute>} />
          <Route path="/cases/:scenarioId/role-play" element={<ProtectedRoute><OsceRolePlayPage /></ProtectedRoute>} />
          <Route path="/flashcards" element={<ProtectedRoute><FlashcardsPage /></ProtectedRoute>} />
          <Route path="/mock-exam" element={<ProtectedRoute><MockExamPage /></ProtectedRoute>} />
          <Route path="/trials" element={<ProtectedRoute><TrialsPage /></ProtectedRoute>} />
          <Route path="/trials/:trialNumber" element={<ProtectedRoute><TrialRunnerPage /></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
          <Route path="/community/:postId" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
          <Route path="/study-plan" element={<ProtectedRoute><StudyPlanPage /></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

          {/* Legal pages (public) */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="/mock-event" element={<MockEventPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/guides/:slug" element={<GuideSlugRouter />} />

          {/* Admin (gated by is_admin inside AdminLayout) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="tracking" element={<AdminTrackingPage />} />
            <Route path="missed-trials" element={<AdminMissedTrialsPage />} />
            <Route path="mcqs" element={<AdminMcqsPage />} />
            <Route path="scenarios" element={<AdminScenariosPage />} />
            <Route path="topics" element={<AdminTopicsPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="promo-codes" element={<AdminPromoCodesPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="support/:ticketId" element={<AdminTicketDetailPage />} />
            <Route path="lessons-review" element={<AdminLessonsReviewPage />} />
            <Route path="lessons-review/:draftId" element={<AdminLessonDetailPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="content-sync" element={<AdminContentSyncPage />} />
            <Route path="marketing" element={<AdminMarketingPage />} />
            <Route path="abandoned-checkouts" element={<AdminAbandonedCheckoutsPage />} />
            <Route path="funnel" element={<AdminFunnelPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="site-settings" element={<AdminSiteSettingsPage />} />
            <Route path="site-content" element={<AdminSiteContentPage />} />
            <Route path="leads" element={<AdminLeadsPage />} />
            <Route path="question-audit" element={<AdminQuestionAuditPage />} />
            <Route path="question-repair" element={<AdminQuestionRepairPage />} />
          </Route>
          {/* CMS pages (type "page") — must be after all built-in routes */}
          <Route path="/:slug" element={<Suspense fallback={null}><DynamicPage type="page" /></Suspense>} />
        </Routes>
      </AppLayout>
      <AuthAwareMentorChat />
      <EarnedTrialModal />
    </BrowserRouter>
  );
}

// MentorChat (Smart Tutor) - Pro-only, study pages only.
// Free users see a small upsell pill instead so they discover the feature.
const STUDY_PAGE_PREFIXES = [
  "/dashboard", "/learn", "/practice", "/cases", "/mock-exam", "/trials", "/community", "/progress", "/study", "/review",
];

const AuthAwareMentorChat = () => {
  const { user, isPro } = useAuth();
  const location = useLocation();
  if (!user) return null;
  const onStudyPage = STUDY_PAGE_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!onStudyPage) return null;
  if (!isPro) return <SmartTutorUpsell />;
  return <MentorChat />;
};

const SmartTutorUpsell = () => {
  const navigate = useNavigate();
  return (
    <button
      data-testid="smart-tutor-upsell"
      onClick={() => navigate("/account?tab=billing")}
      className="fixed bottom-24 md:bottom-16 right-4 md:right-6 z-40 md:z-[60] inline-flex items-center gap-2 px-4 py-3 rounded-full bg-card border border-primary/30 text-sm font-medium text-foreground shadow-xl hover:bg-primary/10 hover:border-primary/60 transition-all"
      title="Smart Tutor is a Pro feature"
    >
      <Lock className="w-4 h-4 text-primary" />
      Unlock Smart Tutor
    </button>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--foreground))",
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
}

export default App;
